import { ActionIcon, Group, ScrollArea, Stack, type StackProps } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { useEnsureUsers, useUsers } from "@/hooks"
import { UserSelect } from "../forms"
import { UserLine } from "../people"
import { icons, NoData } from "../utils"

interface IProps extends Omit<StackProps, "onChange"> {
  user_ids?: string[]
  onChange?: (value: string[]) => void
}

export function UsersPanel({ user_ids = [], onChange, ...props }: IProps) {
  const { t } = useTranslation()
  useEnsureUsers(user_ids)

  return (
    <Stack w="100%" {...props}>
      <ScrollArea.Autosize h={150} offsetScrollbars type="auto">
        <Stack>
          {user_ids.length === 0 && <NoData my="xl" />}
          {user_ids.map((user_id) => (
            <UserRow
              key={user_id}
              user_id={user_id}
              onRemove={(user_id) => {
                onChange?.(user_ids.filter((id) => id !== user_id))
              }}
            />
          ))}
        </Stack>
      </ScrollArea.Autosize>

      <UserSelect
        w="100%"
        mt="auto"
        value={null}
        exclude={user_ids}
        placeholder={t("Add")}
        onChange={(value) => {
          if (value && !user_ids.includes(value)) {
            onChange?.([...user_ids, value])
          }
        }}
      />
    </Stack>
  )
}

function UserRow({ user_id, onRemove }: { user_id: string; onRemove: (user_id: string) => void }) {
  const { t } = useTranslation()
  const { findUser } = useUsers()
  const user = findUser(user_id)
  if (!user) return null
  return (
    <Group justify="space-between" gap="xs">
      <UserLine user={user} compact />
      <ActionIcon color="red" variant="subtle" aria-label={t("Delete")} onClick={() => onRemove(user_id)}>
        <icons.Remove size={16} />
      </ActionIcon>
    </Group>
  )
}
